'use client'

import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { useTheme } from 'next-themes'
import FinancialChart, { type ChartType, type OhlcvDataPoint, type OhlcvCrosshair } from '@/components/FinancialChart'
import { fetchCryptoPrices, fetchCryptoChartData, type CryptoPrice } from '@/services/crypto'
import { useSignalR } from '@/hooks/useSignalR'
import type { PriceUpdate } from '@/services/signalrService'
import InstrumentAbout from '@/components/InstrumentAbout'
import BrokerRecommendations from '@/components/BrokerRecommendations'
import { cryptoBrokersAll } from '@/data/indexData'

interface Props {
  symbol: string
}

const periods = [
  { key: '1H', tr: '1H', en: '1W' },
  { key: '1A', tr: '1A', en: '1M' },
  { key: '3A', tr: '3A', en: '3M' },
  { key: '1Y', tr: '1Y', en: '1Y' },
  { key: '5Y', tr: '5Y', en: '5Y' },
  { key: 'all', tr: 'Tümü', en: 'All' },
]

const cryptoNames: Record<string, string> = {
  BTC: 'Bitcoin', ETH: 'Ethereum', XRP: 'XRP', BNB: 'BNB', SOL: 'Solana',
  DOGE: 'Dogecoin', ADA: 'Cardano', TRX: 'TRON', AVAX: 'Avalanche', SHIB: 'Shiba Inu',
  TON: 'Toncoin', DOT: 'Polkadot', LINK: 'Chainlink', BCH: 'Bitcoin Cash', NEAR: 'NEAR Protocol',
  LTC: 'Litecoin', UNI: 'Uniswap', ICP: 'Internet Computer', MATIC: 'Polygon', ATOM: 'Cosmos',
  XLM: 'Stellar', ETC: 'Ethereum Classic', HBAR: 'Hedera', FIL: 'Filecoin', APT: 'Aptos',
  ARB: 'Arbitrum', VET: 'VeChain', ALGO: 'Algorand', GRT: 'The Graph', OP: 'Optimism',
  AAVE: 'Aave', SUI: 'Sui', SAND: 'The Sandbox', MANA: 'Decentraland', AXS: 'Axie Infinity',
  EOS: 'EOS', THETA: 'Theta Network', FLOW: 'Flow', STX: 'Stacks', XTZ: 'Tezos',
  RUNE: 'THORChain', FTM: 'Fantom', MINA: 'Mina Protocol', ZEC: 'Zcash', XMR: 'Monero',
  DASH: 'Dash', NEO: 'NEO', CHZ: 'Chiliz', ENJ: 'Enjin Coin', SNX: 'Synthetix',
  CRV: 'Curve DAO Token', COMP: 'Compound', YFI: 'yearn.finance', BAT: 'Basic Attention Token', SUSHI: 'SushiSwap',
  ZIL: 'Zilliqa', LRC: 'Loopring', KSM: 'Kusama', KAVA: 'Kava', ANKR: 'Ankr',
  OCEAN: 'Ocean Protocol', STORJ: 'Storj', APE: 'ApeCoin', IMX: 'Immutable', MKR: 'Maker',
  EGLD: 'MultiversX', ROSE: 'Oasis Network', HNT: 'Helium', WLD: 'Worldcoin', INJ: 'Injective',
  SEI: 'Sei', TIA: 'Celestia', RNDR: 'Render', FET: 'Fetch.ai', AR: 'Arweave',
  KAS: 'Kaspa', PEPE: 'Pepe', WIF: 'dogwifhat', BONK: 'Bonk', FLOKI: 'FLOKI',
  GALA: 'Gala', HOT: 'Holo', MASK: 'Mask Network', GLMR: 'Moonbeam', JASMY: 'JasmyCoin',
  AUDIO: 'Audius', RSR: 'Reserve Rights', NMR: 'Numeraire', DCR: 'Decred', IOTA: 'IOTA',
  WAVES: 'Waves', CFX: 'Conflux', PYTH: 'Pyth Network', JUP: 'Jupiter', STRK: 'Starknet',
  W: 'Wormhole', AGIX: 'SingularityNET', NOT: 'Notcoin', CAKE: 'PancakeSwap', LUNA: 'Terra',
}

function fmtUsd(val: number): string {
  if (val === 0) return '\u2014'
  const formatted = val.toFixed(2)
  const [int, dec] = formatted.split('.')
  return '$' + int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec
}

function fmtVol(vol: number): string {
  if (vol >= 1_000_000_000) return '$' + (vol / 1_000_000_000).toFixed(2) + 'B'
  if (vol >= 1_000_000) return '$' + (vol / 1_000_000).toFixed(2) + 'M'
  if (vol >= 1_000) return '$' + (vol / 1_000).toFixed(1) + 'K'
  return '$' + vol.toLocaleString('en-US')
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

/* Chart Type Icons */
function IconCandle({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="2" x2="8" y2="6" /><rect x="5.5" y="6" width="5" height="10" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="8" y1="16" x2="8" y2="22" />
      <line x1="17" y1="4" x2="17" y2="8" /><rect x="14.5" y="8" width="5" height="8" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="17" y1="16" x2="17" y2="20" />
    </svg>
  )
}

function IconLine({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 18 8 12 14 16 22 6" />
    </svg>
  )
}

function IconArea({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20 L8 12 L14 16 L22 6 L22 20 Z" fill={active ? 'rgba(52,152,219,0.2)' : 'rgba(128,128,128,0.1)'} />
      <polyline points="2 20 8 12 14 16 22 6" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" fill="none" />
    </svg>
  )
}

export default function CryptoDetailClient({ symbol: symbolParam }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const symbol = (symbolParam || '').toUpperCase()

  const [quote, setQuote] = useState<CryptoPrice | null>(null)
  const [ohlcvData, setOhlcvData] = useState<OhlcvDataPoint[]>([])
  const [period, setPeriod] = useState('1A')
  const [chartType, setChartType] = useState<ChartType>('candlestick')
  const [crosshair, setCrosshair] = useState<OhlcvCrosshair | null>(null)
  const [chartLoading, setChartLoading] = useState(true)
  const [chartUpdatedAt, setChartUpdatedAt] = useState<number>(0)

  const displayName = quote?.name || cryptoNames[symbol] || symbol

  const loadQuote = useCallback(async () => {
    if (!symbol) return
    const data = await fetchCryptoPrices([symbol])
    const q = data[symbol]
    if (q) setQuote(q)
  }, [symbol])

  useEffect(() => {
    loadQuote()
    const iv = setInterval(loadQuote, 60_000)
    return () => clearInterval(iv)
  }, [loadQuote])

  // SignalR realtime — backend BTCUSD gönderir, biz BTC ile karşılaştırırız
  const handlePriceUpdate = useCallback((update: PriceUpdate) => {
    const sym = update.symbol.endsWith('USD') ? update.symbol.slice(0, -3) : update.symbol
    if (sym !== symbol) return
    setQuote(prev => prev ? { ...prev, usd: update.price } : prev)
  }, [symbol])

  useSignalR({ onPriceUpdate: handlePriceUpdate })

  // OHLCV chart data — CoinGecko üzerinden, tüm kriptolar için çalışır
  useEffect(() => {
    if (!symbol) return
    setChartLoading(true)
    setCrosshair(null)
    fetchCryptoChartData(symbol, period).then(points => {
      setOhlcvData(points.map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
        volume: null,
      })))
      setChartUpdatedAt(Date.now())
      setChartLoading(false)
    }).catch(() => { setOhlcvData([]); setChartLoading(false) })
  }, [symbol, period])

  if (!symbol) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4">
        <div className="text-center py-24">
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            {lang === 'en' ? 'Crypto not found' : 'Kripto bulunamadı'}
          </h1>
          <button
            onClick={() => router.push('/')}
            className="px-6 py-2.5 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
          >
            {lang === 'en' ? 'Back to Home' : 'Ana Sayfaya Dön'}
          </button>
        </div>
      </main>
    )
  }

  const isUp = quote ? quote.usd_24h_change >= 0 : true
  const clr = isUp ? 'var(--chart-up)' : 'var(--chart-down)'

  // OHLC display data (crosshair or last data)
  const lastOhlcv = ohlcvData.length > 0 ? ohlcvData[ohlcvData.length - 1] : null
  const ohlcDisplay = crosshair || (lastOhlcv ? {
    time: lastOhlcv.time,
    open: lastOhlcv.open,
    high: lastOhlcv.high,
    low: lastOhlcv.low,
    close: lastOhlcv.close,
    volume: lastOhlcv.volume,
  } : null)

  return (
    <main className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[12px] mb-4 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors" style={{ color: 'var(--accent-color)' }}>
          {lang === 'en' ? 'Home' : 'Anasayfa'}
        </Link>
        <span className="select-none">/</span>
        <Link href="/crypto" className="hover:underline transition-colors" style={{ color: 'var(--accent-color)' }}>
          {lang === 'en' ? 'Crypto' : 'Kripto'}
        </Link>
        <span className="select-none">/</span>
        <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{symbol}</span>
      </nav>

      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight m-0 flex items-baseline gap-2" style={{ color: 'var(--text-main)' }}>
            {symbol}/USD
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              ({displayName})
            </span>
          </h1>
        </div>
        {quote ? (
          <div className="flex items-baseline gap-3 shrink-0">
            <span className="text-[28px] font-bold tabular-nums" style={{ color: clr }}>
              {fmtUsd(quote.usd)}
            </span>
            <span className="text-[15px] font-semibold tabular-nums" style={{ color: clr }}>
              {isUp ? '+' : ''}%{Math.abs(quote.usd_24h_change).toFixed(2).replace('.', ',')}
              <svg
                className="inline-block ml-1"
                width="10" height="10" viewBox="0 0 10 10"
                fill={clr}
              >
                {isUp
                  ? <polygon points="5,1 9,7 1,7" />
                  : <polygon points="5,9 9,3 1,3" />
                }
              </svg>
              <span className="text-[12px] ml-1" style={{ color: 'var(--text-muted)' }}>(24h)</span>
            </span>
          </div>
        ) : (
          <div className="flex items-baseline gap-3">
            <Skeleton w={140} h={28} />
            <Skeleton w={100} h={18} />
          </div>
        )}
      </div>

      {/* Chart Card */}
      <div
        className="rounded-xl border overflow-hidden mb-5"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        {/* OHLC Data Row */}
        {ohlcDisplay && (
          <div
            className="flex items-center gap-4 sm:gap-6 px-4 sm:px-5 pt-3 pb-2 flex-wrap"
            style={{ borderBottom: '1px solid var(--border-color)' }}
          >
            {[
              { label: 'O', value: ohlcDisplay.open },
              { label: 'H', value: ohlcDisplay.high, color: 'var(--chart-up)' },
              { label: 'L', value: ohlcDisplay.low, color: 'var(--chart-down)' },
              { label: 'C', value: ohlcDisplay.close },
            ].map(item => (
              <div key={item.label} className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: item.color || 'var(--text-main)' }}>
                  {fmtUsd(item.value)}
                </span>
              </div>
            ))}
            {ohlcDisplay.volume != null && ohlcDisplay.volume > 0 && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Vol</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>
                  {fmtVol(ohlcDisplay.volume)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Toolbar: Periods + Chart Type Toggle */}
        <div
          className="flex items-center justify-between px-4 sm:px-5 py-2"
          style={{ borderBottom: '1px solid var(--border-color)' }}
        >
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {periods.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className="px-3 py-1 rounded-md text-[12px] font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: period === p.key ? 'var(--accent-color)' : 'transparent',
                  color: period === p.key ? '#fff' : 'var(--text-muted)',
                }}
              >
                {lang === 'en' ? p.en : p.tr}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0.5 ml-3 shrink-0">
            <div className="h-4 w-px mx-2" style={{ backgroundColor: 'var(--border-color)' }} />
            {([
              { type: 'candlestick' as ChartType, Icon: IconCandle },
              { type: 'line' as ChartType, Icon: IconLine },
              { type: 'area' as ChartType, Icon: IconArea },
            ]).map(({ type, Icon }) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className="p-1.5 rounded transition-colors"
                style={{
                  backgroundColor: chartType === type ? (isDark ? 'rgba(52,152,219,0.15)' : 'rgba(52,152,219,0.1)') : 'transparent',
                }}
                title={type}
              >
                <Icon active={chartType === type} />
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="px-0">
          {chartLoading ? (
            <div className="animate-pulse" style={{ height: 480, backgroundColor: 'var(--border-color)', opacity: 0.15 }} />
          ) : ohlcvData.length === 0 ? (
            <div className="flex items-center justify-center" style={{ height: 480 }}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {lang === 'en' ? 'No chart data available' : 'Grafik verisi bulunamadı'}
              </span>
            </div>
          ) : (
            <FinancialChart
              data={ohlcvData}
              isDark={isDark}
              height={480}
              chartType={chartType}
              showVolume={true}
              onCrosshairMove={setCrosshair}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-2 text-[11px]" style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--border-color)' }}>
          {lang === 'en' ? 'Last updated' : 'Son güncelleme'}: {chartUpdatedAt > 0
            ? (() => {
                const tz = 'Europe/Istanbul'
                const d = new Date(chartUpdatedAt)
                return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { timeZone: tz }) + ' ' + d.toLocaleTimeString(lang === 'en' ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: tz })
              })()
            : '\u2014'}
        </div>
      </div>

      {/* Info Card */}
      {quote && (
        <div
          className="rounded-xl border overflow-hidden mb-5 px-5 py-4"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {[
              { label: lang === 'en' ? 'Price' : 'Fiyat', value: fmtUsd(quote.usd), color: 'var(--text-main)' },
              { label: lang === 'en' ? '24h Change' : '24s Değişim', value: `${quote.usd_24h_change >= 0 ? '+' : ''}%${Math.abs(quote.usd_24h_change).toFixed(2)}`, color: clr },
              ...(quote.marketCap ? [{ label: lang === 'en' ? 'Market Cap' : 'Piyasa Değeri', value: fmtVol(quote.marketCap), color: 'var(--text-main)' }] : []),
              ...(quote.volume24h ? [{ label: lang === 'en' ? '24h Volume' : '24s Hacim', value: fmtVol(quote.volume24h), color: 'var(--text-main)' }] : []),
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
                <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <InstrumentAbout symbol={symbol} kind="crypto" />

      {/* Kripto borsaları — Türkiye + Global tek bölümde */}
      <BrokerRecommendations
        brokers={cryptoBrokersAll}
        source="crypto_detail"
        customHeadline={lang === 'en'
          ? `💸 Ready to buy ${symbol}?`
          : `💸 ${symbol} Almaya Hazır mısın?`}
        customSubline={lang === 'en'
          ? 'Compare Turkey-based (TRY deposit) and global crypto exchanges. Open an account in minutes — zero & low commission options available.'
          : 'Türkiye merkezli (TL para yatırma) ve global kripto borsalarını karşılaştır. Dakikalar içinde hesap aç — sıfır ve düşük komisyon seçenekleri mevcut.'}
        customCta={lang === 'en' ? 'Open a crypto account →' : 'Kripto Hesabı Aç →'}
      />
    </main>
  )
}
